import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, CheckCircle2, Clock, FileText, Image as ImageIcon,
  Building2, Sparkles, ShieldCheck, PenLine, Calendar,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import ProviderParty, { contractBrand } from "../../components/contracts/ProviderParty";

const SERVICE_LABELS = {
  cleaning:    "Limpeza",
  supervision: "Supervisão",
  checkin:     "Preparação Check-in",
  laundry:     "Lavandaria",
  reports:     "Relatórios de supervisão",
};

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (withTime) return d.toLocaleString("pt-PT");
    return d.toLocaleDateString("pt-PT");
  } catch { return iso; }
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5 sm:p-6"
    >
      <h2 className="flex items-center gap-2 font-display text-base sm:text-lg text-[#fdfbf7] border-l-2 border-[#d4af37] pl-3">
        {Icon && <Icon size={16} className="text-[#d4af37]" />} {title}
      </h2>
      <div className="mt-4 text-sm text-[#cfd8d6] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#7c8e8b]">{k}</div>
      <div className="text-[#fdfbf7]">{v || "—"}</div>
    </div>
  );
}

function Clause({ n, title, children }) {
  return (
    <div className="space-y-1.5">
      <h3 className="font-display text-[#d4af37] text-sm">
        Cláusula {n}.ª · {title}
      </h3>
      <div className="text-[13px] text-[#cfd8d6] leading-relaxed text-justify space-y-1.5">
        {children}
      </div>
    </div>
  );
}


export default function ClientContractView() {
  const { cid } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/client/contracts/${cid}`);
        setC(data);
      } catch (e) {
        toast.error(formatApiError(e, "Não foi possível abrir o contrato"));
      } finally { setLoading(false); }
    })();
  }, [cid]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="client-contract-view-loading">
        <div className="h-10 w-40 rounded-md border border-[#1a4640] bg-[#062c27] animate-pulse" />
        {[0, 1, 2].map(i => <div key={i} className="h-32 rounded-xl border border-[#1a4640] bg-[#062c27] animate-pulse" />)}
      </div>
    );
  }
  if (!c) {
    return (
      <div className="rounded-2xl border border-dashed border-[#1a4640] bg-[#062c27] py-16 text-center" data-testid="client-contract-view-empty">
        <FileText size={32} className="mx-auto text-[#1a4640]" />
        <p className="mt-4 text-sm text-[#a0b2af]">Contrato não encontrado.</p>
        <Link to="/cliente/contratos" className="inline-block mt-4 text-xs uppercase tracking-wider text-[#d4af37] hover:underline">
          Voltar aos contratos
        </Link>
      </div>
    );
  }

  const client = c.client || {};
  const signed = c.status === "signed";
  const includedProps = (c.properties || []).filter(p => p.included);
  const signedAt = c.signed_at || c.signature?.signed_at;
  const sigMethod = c.signature?.method;
  const sigData = c.signature?.data;
  const number = (c.id || "").slice(0, 8).toUpperCase();
  const imgAuthYes = c.image_authorization === "yes";

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="client-contract-view">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/cliente/contratos"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#a0b2af] hover:text-[#d4af37] transition-colors"
          data-testid="client-contract-view-back"
        >
          <ArrowLeft size={14} /> Voltar
        </Link>
        {signed && (
          <a
            href={`${BACKEND_URL}/api/client/contracts/${cid}/pdf`}
            target="_blank" rel="noopener noreferrer"
            data-testid="client-contract-view-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all"
          >
            <Download size={12} /> Descarregar PDF
          </a>
        )}
      </div>

      {/* Title + status */}
      <div className="rounded-2xl border border-[#1a4640] bg-gradient-to-br from-[#062c27] to-[#04201c] p-6 sm:p-8">
        <div className="text-[10px] tracking-[0.18em] uppercase text-[#7c8e8b]">Contrato de Prestação de Serviços</div>
        <h1 className="mt-1 font-display text-2xl sm:text-3xl text-[#fdfbf7]">
          Contrato Nº <span className="italic text-[#d4af37]">{number}</span>
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {signed ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/40">
              <CheckCircle2 size={11} /> Assinado em {fmtDate(signedAt, true)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#3a2f0a] text-[#e8c860] border border-[#d4af37]/40">
              <Clock size={11} /> {c.status === "sent" ? "Pendente de assinatura" : "Rascunho"}
            </span>
          )}
          {c.image_authorization && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
              imgAuthYes ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                          : "bg-red-500/10 text-red-300 border-red-500/40"
            }`}>
              <ImageIcon size={10} /> {imgAuthYes ? "Autoriza imagens" : "Não autoriza imagens"}
            </span>
          )}
        </div>
      </div>

      {/* Identification */}
      <Section icon={ShieldCheck} title="Identificação das Partes" testId="contract-section-parties">
        <div className="grid sm:grid-cols-2 gap-4">
          <ProviderParty contract={c} className="rounded-lg border border-[#1a4640] p-4" textClass="text-[12px]" />
          <div className="rounded-lg border border-[#1a4640] p-4">
            <div className="text-[10px] tracking-[0.16em] uppercase text-[#d4af37] font-semibold">Segunda Outorgante · Cliente</div>
            <div className="mt-1 font-display text-base text-[#fdfbf7]">{client.name || "—"}</div>
            <div className="mt-2 text-[12px] text-[#cfd8d6] leading-relaxed">
              <strong>NIF:</strong> {client.nif || "—"}<br />
              <strong>Morada:</strong> {client.address || "—"}<br />
              <strong>Email:</strong> {client.email || "—"}<br />
              <strong>Telefone:</strong> {client.phone || "—"}
            </div>
          </div>
        </div>
      </Section>

      <Clause n="1" title="Objeto do Contrato">
        <p>1.1. A {contractBrand(c)} obriga-se a prestar os serviços profissionais discriminados na Cláusula 3.ª, com vista à manutenção, higienização, supervisão operacional e preparação das propriedades identificadas na Cláusula 2.ª para utilização em regime de alojamento local, Airbnb, Booking.com, Vrbo ou plataformas equivalentes.</p>
        <p>1.2. Os serviços respeitam os padrões de qualidade do setor de hospitalidade premium, em conformidade com a legislação aplicável em Portugal.</p>
      </Clause>

      <Section icon={Building2} title="Cláusula 2.ª · Propriedades Abrangidas" testId="contract-section-properties">
        {c.global_scope ? (
          <div className="rounded-md border border-[#d4af37]/40 bg-[#fff7e0]/5 p-3 text-[#e8c860]">
            Aplica-se a <strong>todas as propriedades atuais e futuras</strong> associadas à conta do Cliente.
          </div>
        ) : includedProps.length === 0 ? (
          <p className="text-[#7c8e8b] italic">Nenhuma propriedade selecionada.</p>
        ) : (
          <ul className="divide-y divide-[#1a4640] rounded-md border border-[#1a4640] overflow-hidden">
            {includedProps.map((p, i) => (
              <li key={i} className="px-3 py-2 grid sm:grid-cols-3 gap-1 text-[13px]">
                <span className="text-[#fdfbf7] font-medium">{p.name || "—"}</span>
                <span className="text-[#a0b2af]">{p.code || "—"}</span>
                <span className="text-[#a0b2af]">{p.address || "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={Sparkles} title="Cláusula 3.ª · Serviços Contratados" testId="contract-section-services">
        <div className="flex flex-wrap gap-2">
          {Object.entries(SERVICE_LABELS).map(([k, label]) => {
            const on = !!c.services?.[k];
            return (
              <span
                key={k}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] border ${
                  on ? "bg-[#d4af37]/10 border-[#d4af37]/50 text-[#e8c860]"
                     : "bg-transparent border-[#1a4640] text-[#7c8e8b] line-through"
                }`}
              >
                {on && <CheckCircle2 size={11} />} {label}
              </span>
            );
          })}
        </div>
        {c.services?.other && (
          <p className="mt-3 text-[12px] text-[#a0b2af]">
            <strong>Outros serviços:</strong> {c.services.other}
          </p>
        )}
      </Section>

      <Clause n="4" title="Preços e Condições de Pagamento">
        <p>4.1. Os preços seguem a tabela vigente da {contractBrand(c)} (luxstaymadeira.pt/precos), aplicáveis aos escalões de tipologia (T0 – T4 / Villa).</p>
        <p>4.2. Aos preços acresce IVA à taxa legal em vigor, quando aplicável.</p>
        <p>4.3. Pagamentos efetuados após conclusão do serviço, no prazo máximo de 5 dias úteis, por <strong>transferência bancária</strong> ou <strong>MB WAY</strong>.</p>
        <p>4.4. Em propriedades premium, poderá ser exigido sinal não inferior a 30% do valor total.</p>
        <p>4.5. O incumprimento confere o direito de suspender a prestação até regularização.</p>
      </Clause>

      <Clause n="5" title="Vigência, Cancelamento e Rescisão">
        <p>5.1. O contrato vigora a partir da data da assinatura digital, por tempo indeterminado.</p>
        <p>5.2. O Cliente pode rescindir mediante <strong>aviso prévio mínimo de 15 dias</strong>, por email para luxstaymadeira@gmail.com.</p>
        <p>5.3. Cancelamentos com menos de 48h podem ser cobrados a 50% (parcial) ou 100% (integral).</p>
        <p>5.4. A {contractBrand(c)} pode rescindir com aviso prévio de 30 dias em caso de incumprimento reiterado.</p>
      </Clause>

      <Clause n="6" title="Obrigações do Cliente">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Garantir o acesso atempado e desimpedido às propriedades.</li>
          <li>Comunicar com antecedência razoável as datas de check-in/check-out.</li>
          <li>Manter as propriedades em condições estruturais adequadas.</li>
          <li>Suportar custos de consumíveis e reparações fora do escopo.</li>
          <li>Tratar a equipa com respeito.</li>
        </ol>
      </Clause>

      <Clause n="7" title={`Obrigações da ${contractBrand(c)}`}>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Prestar os serviços com diligência, qualidade e pontualidade.</li>
          <li>Garantir a confidencialidade absoluta (RGPD).</li>
          <li>Reportar de imediato danos, objetos esquecidos ou anomalias.</li>
          <li>Manter equipa qualificada, equipada e supervisionada.</li>
          <li>Disponibilizar canal de contacto Seg–Sáb · 07:00–19:00.</li>
        </ol>
      </Clause>

      <Clause n="8" title="Confidencialidade e Proteção de Dados (RGPD)">
        <p>Cada parte trata com estrita confidencialidade as informações a que tem acesso. A {contractBrand(c)} atua, no que respeita a dados de hóspedes, como subcontratante de tratamento, sob instruções do Cliente. Política completa em luxstaymadeira.pt/privacidade.</p>
      </Clause>

      <Section icon={ImageIcon} title="Cláusula 9.ª · Autorização de Utilização de Imagens" testId="contract-section-image-auth">
        <div className={`rounded-lg border-2 p-4 ${
          imgAuthYes ? "border-emerald-500/50 bg-emerald-500/5"
                     : "border-red-500/50 bg-red-500/5"
        }`}>
          <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c8e8b]">Decisão expressa do Cliente</div>
          <div className={`mt-1 font-display text-base ${
            imgAuthYes ? "text-emerald-300" : "text-red-300"
          }`}>
            {imgAuthYes
              ? "AUTORIZA a utilização das imagens das propriedades para fins promocionais."
              : c.image_authorization === "no"
                ? "NÃO AUTORIZA a utilização de imagens das propriedades para fins promocionais."
                : "—"}
          </div>
          <p className="mt-2 text-[12px] text-[#a0b2af]">
            A autorização cobre fotografias e vídeos das propriedades em materiais promocionais
            (site, redes sociais, portfólio), sem objetos pessoais identificáveis dos hóspedes.
            Pode ser revogada por escrito a qualquer momento.
          </p>
        </div>
      </Section>

      <Clause n="10" title="Responsabilidade Civil">
        <p>A {contractBrand(c)} é responsável por danos causados de forma direta e exclusiva pela sua atuação dolosa ou gravemente negligente, até ao valor do serviço, com o máximo de 2.500€ por evento. Reclamações devem ser comunicadas por escrito no prazo máximo de 7 dias após a execução do serviço.</p>
      </Clause>

      <Clause n="11" title="Comunicações">
        <p>Comunicações vinculativas por email para os endereços indicados na identificação das partes. Comunicações urgentes podem ser por WhatsApp/telefone, sendo confirmadas por email se envolverem alterações contratuais.</p>
      </Clause>

      <Clause n="12" title="Foro e Lei Aplicável">
        <p>O presente contrato é regido pela <strong>lei portuguesa</strong>. Para resolução de litígios, é competente o foro da Comarca da Madeira (Funchal). As partes comprometem-se a procurar uma solução amigável antes de recorrer à via judicial.</p>
      </Clause>

      {c.notes && (
        <Section icon={FileText} title="Notas Adicionais" testId="contract-section-notes">
          <p className="whitespace-pre-line">{c.notes}</p>
        </Section>
      )}

      {/* Signature */}
      <Section icon={PenLine} title="Aceitação e Assinatura" testId="contract-section-signature">
        <p>O Cliente declarou, mediante a assinatura digital aposta abaixo, ter lido,
        compreendido e aceite integralmente todos os termos e condições do presente contrato,
        incluindo a sua decisão expressa quanto à utilização de imagens (Cláusula 9.ª) e a
        cláusula de cancelamento (Cláusula 5.ª).</p>

        {signed ? (
          <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5">
            <div className="flex items-center gap-2 text-emerald-300 text-[12px] uppercase tracking-wider font-semibold">
              <CheckCircle2 size={14} /> Confirmação de aceitação
            </div>
            <p className="mt-2 text-[13px] text-emerald-100/80">
              O Cliente aceitou expressamente os termos no momento da assinatura digital.
            </p>

            <div className="mt-5 rounded-lg border border-[#1a4640] bg-[#04201c] p-4">
              <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c8e8b]">Assinatura do Cliente</div>
              <div className="mt-3 min-h-[100px] flex items-center justify-center border-b border-[#1a4640] pb-4">
                {sigMethod === "drawn" && typeof sigData === "string" && sigData.startsWith("data:image") ? (
                  <img src={sigData} alt="Assinatura" className="max-h-[110px]" data-testid="contract-signature-image" />
                ) : sigMethod === "typed" && sigData ? (
                  <div className="text-3xl text-[#fdfbf7]" style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive" }} data-testid="contract-signature-typed">
                    {sigData}
                  </div>
                ) : (
                  <div className="text-[#7c8e8b]">—</div>
                )}
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px] text-[#a0b2af]">
                <KV k="Assinado em" v={fmtDate(signedAt, true)} />
                {c.signature?.ip && <KV k="IP" v={c.signature.ip} />}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-[#d4af37]/40 bg-[#3a2f0a]/40 p-3 text-[#e8c860] text-[12px]">
            <Clock size={12} className="inline mr-1" />
            Este contrato ainda não foi assinado.
            {c.token && (
              <Link
                to={`/contratos/assinar/${c.token}`}
                className="ml-2 underline font-semibold hover:text-[#fdfbf7]"
                data-testid="contract-go-sign"
              >
                Assinar agora →
              </Link>
            )}
          </div>
        )}
      </Section>

      <div className="rounded-md border border-[#1a4640] bg-[#062c27] p-3 text-[11px] text-[#7c8e8b] flex items-center gap-2">
        <Calendar size={12} />
        Documento criado a {fmtDate(c.created_at)}
        {c.sent_at && ` · enviado a ${fmtDate(c.sent_at)}`}
        {signedAt && ` · assinado a ${fmtDate(signedAt, true)}`}
      </div>
    </div>
  );
}
